import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { recheckProviderRequirements } from '../../util/recheckProviderRequirements';
import {
  syncProviderServices,
  pickCanonicalListing,
  getActiveCategoryIds,
  hasMismatchedSharedFields,
} from '../../util/providerListingSync';

// ================ Action types ================ //

export const FETCH_OWN_LISTINGS_REQUEST = 'app/ProviderServicesPage/FETCH_OWN_LISTINGS_REQUEST';
export const FETCH_OWN_LISTINGS_SUCCESS = 'app/ProviderServicesPage/FETCH_OWN_LISTINGS_SUCCESS';
export const FETCH_OWN_LISTINGS_ERROR = 'app/ProviderServicesPage/FETCH_OWN_LISTINGS_ERROR';

export const FETCH_EXCEPTIONS_REQUEST = 'app/ProviderServicesPage/FETCH_EXCEPTIONS_REQUEST';
export const FETCH_EXCEPTIONS_SUCCESS = 'app/ProviderServicesPage/FETCH_EXCEPTIONS_SUCCESS';
export const FETCH_EXCEPTIONS_ERROR = 'app/ProviderServicesPage/FETCH_EXCEPTIONS_ERROR';

export const SAVE_SERVICES_REQUEST = 'app/ProviderServicesPage/SAVE_SERVICES_REQUEST';
export const SAVE_SERVICES_SUCCESS = 'app/ProviderServicesPage/SAVE_SERVICES_SUCCESS';
export const SAVE_SERVICES_ERROR = 'app/ProviderServicesPage/SAVE_SERVICES_ERROR';
export const CLEAR_SAVE_RESULT = 'app/ProviderServicesPage/CLEAR_SAVE_RESULT';

export const FETCH_BOOKINGS_REQUEST = 'app/ProviderServicesPage/FETCH_BOOKINGS_REQUEST';
export const FETCH_BOOKINGS_SUCCESS = 'app/ProviderServicesPage/FETCH_BOOKINGS_SUCCESS';
export const FETCH_BOOKINGS_ERROR = 'app/ProviderServicesPage/FETCH_BOOKINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  listings: [],
  listingsFetchInProgress: false,
  listingsFetchError: null,
  canonicalListingId: null,
  activeCategoryIds: [],
  hasMismatchedSharedFields: false,
  exceptions: [],
  exceptionsFetchInProgress: false,
  exceptionsFetchError: null,
  saveInProgress: false,
  saveError: null,
  saveResults: null,
  bookingTransactions: [],
  bookingsFetchInProgress: false,
  bookingsFetchError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_OWN_LISTINGS_REQUEST:
      return {
        ...state,
        listingsFetchInProgress: true,
        listingsFetchError: null,
      };
    case FETCH_OWN_LISTINGS_SUCCESS: {
      const listings = payload.listings || [];
      const canonical = pickCanonicalListing(listings);
      return {
        ...state,
        listingsFetchInProgress: false,
        listings,
        canonicalListingId: canonical?.id?.uuid || null,
        activeCategoryIds: getActiveCategoryIds(listings),
        hasMismatchedSharedFields: hasMismatchedSharedFields(listings, canonical),
      };
    }
    case FETCH_OWN_LISTINGS_ERROR:
      return {
        ...state,
        listingsFetchInProgress: false,
        listingsFetchError: payload,
      };

    case FETCH_EXCEPTIONS_REQUEST:
      return {
        ...state,
        exceptionsFetchInProgress: true,
        exceptionsFetchError: null,
      };
    case FETCH_EXCEPTIONS_SUCCESS:
      return {
        ...state,
        exceptionsFetchInProgress: false,
        exceptions: payload.exceptions || [],
      };
    case FETCH_EXCEPTIONS_ERROR:
      return {
        ...state,
        exceptionsFetchInProgress: false,
        exceptionsFetchError: payload,
        exceptions: [],
      };

    case SAVE_SERVICES_REQUEST:
      return {
        ...state,
        saveInProgress: true,
        saveError: null,
        saveResults: null,
      };
    case SAVE_SERVICES_SUCCESS:
      return {
        ...state,
        saveInProgress: false,
        saveResults: payload,
      };
    case SAVE_SERVICES_ERROR:
      return {
        ...state,
        saveInProgress: false,
        saveError: payload,
      };
    case CLEAR_SAVE_RESULT:
      return {
        ...state,
        saveError: null,
        saveResults: null,
      };

    case FETCH_BOOKINGS_REQUEST:
      return {
        ...state,
        bookingsFetchInProgress: true,
        bookingsFetchError: null,
      };
    case FETCH_BOOKINGS_SUCCESS:
      return {
        ...state,
        bookingsFetchInProgress: false,
        bookingTransactions: payload.transactions || [],
      };
    case FETCH_BOOKINGS_ERROR:
      return {
        ...state,
        bookingsFetchInProgress: false,
        bookingsFetchError: payload,
        bookingTransactions: [],
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchOwnListingsRequest = () => ({ type: FETCH_OWN_LISTINGS_REQUEST });
export const fetchOwnListingsSuccess = listings => ({
  type: FETCH_OWN_LISTINGS_SUCCESS,
  payload: { listings },
});
export const fetchOwnListingsError = e => ({
  type: FETCH_OWN_LISTINGS_ERROR,
  payload: e,
  error: true,
});

export const fetchExceptionsRequest = () => ({ type: FETCH_EXCEPTIONS_REQUEST });
export const fetchExceptionsSuccess = exceptions => ({
  type: FETCH_EXCEPTIONS_SUCCESS,
  payload: { exceptions },
});
export const fetchExceptionsError = e => ({
  type: FETCH_EXCEPTIONS_ERROR,
  payload: e,
  error: true,
});

export const saveServicesRequest = () => ({ type: SAVE_SERVICES_REQUEST });
export const saveServicesSuccess = result => ({
  type: SAVE_SERVICES_SUCCESS,
  payload: result,
});
export const saveServicesError = e => ({
  type: SAVE_SERVICES_ERROR,
  payload: e,
  error: true,
});
export const clearSaveResult = () => ({ type: CLEAR_SAVE_RESULT });

export const fetchBookingsRequest = () => ({ type: FETCH_BOOKINGS_REQUEST });
export const fetchBookingsSuccess = transactions => ({
  type: FETCH_BOOKINGS_SUCCESS,
  payload: { transactions },
});
export const fetchBookingsError = e => ({
  type: FETCH_BOOKINGS_ERROR,
  payload: e,
  error: true,
});

// ================ Thunks ================ //

const OWN_LISTING_STATES = [
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_CLOSED,
];

export const fetchOwnListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchOwnListingsRequest());
  // Sharetribe expects camelCase states (e.g. pendingApproval), not kebab-case.
  return sdk.ownListings
    .query({
      states: OWN_LISTING_STATES,
      perPage: 100,
    })
    .then(response => {
      const listings = denormalisedResponseEntities(response);
      dispatch(fetchOwnListingsSuccess(listings));
      return listings;
    })
    .catch(e => {
      dispatch(fetchOwnListingsError(storableError(e)));
      throw e;
    });
};

export const fetchCanonicalExceptions = listingId => (dispatch, getState, sdk) => {
  if (!listingId) {
    dispatch(fetchExceptionsSuccess([]));
    return Promise.resolve([]);
  }
  dispatch(fetchExceptionsRequest());
  return sdk.availabilityExceptions
    .query({ listingId })
    .then(response => {
      const exceptions = denormalisedResponseEntities(response);
      dispatch(fetchExceptionsSuccess(exceptions));
      return exceptions;
    })
    .catch(e => {
      dispatch(fetchExceptionsError(storableError(e)));
      return [];
    });
};

/**
 * Save services form: sync listings then reload page data.
 */
export const saveProviderServices = ({
  selectedCategoryIds,
  categories,
  sharedFields,
  canonicalExceptions,
  currency,
}) => (dispatch, getState, sdk) => {
  dispatch(saveServicesRequest());
  const { listings } = getState().ProviderServicesPage;

  return syncProviderServices({
    sdk,
    selectedCategoryIds,
    categories,
    userListings: listings,
    sharedFields,
    canonicalExceptions,
    currency,
  })
    .then(result => {
      dispatch(saveServicesSuccess(result));
      dispatch(recheckProviderRequirements({ source: 'provider-services-save' }));
      return dispatch(fetchOwnListings()).then(updatedListings => {
        const canonical = pickCanonicalListing(updatedListings);
        if (canonical?.id) {
          return dispatch(fetchCanonicalExceptions(canonical.id));
        }
        return result;
      }).then(() => result);
    })
    .catch(e => {
      dispatch(saveServicesError(storableError(e)));
      throw e;
    });
};

export const fetchSalesBookings = () => (dispatch, getState, sdk) => {
  dispatch(fetchBookingsRequest());
  return sdk.transactions
    .query({
      only: 'sale',
      lastTransitions: getAllTransitionsForEveryProcess(),
      include: ['listing', 'customer', 'booking'],
      'fields.transaction': [
        'processName',
        'lastTransition',
        'lastTransitionedAt',
        'transitions',
      ],
      'fields.listing': ['title', 'availabilityPlan', 'publicData'],
      'fields.user': [
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.firstName',
        'profile.lastName',
      ],
      page: 1,
      perPage: 50,
    })
    .then(response => {
      const transactions = denormalisedResponseEntities(response);
      dispatch(fetchBookingsSuccess(transactions));
      return transactions;
    })
    .catch(e => {
      dispatch(fetchBookingsError(storableError(e)));
      return [];
    });
};

export const loadData = () => (dispatch, getState, sdk) => {
  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(fetchOwnListings()),
    dispatch(fetchSalesBookings()),
  ]).then(([, listings]) => {
    const canonical = pickCanonicalListing(listings || []);
    if (canonical?.id) {
      return dispatch(fetchCanonicalExceptions(canonical.id));
    }
    return [];
  });
};
